import * as React from "react";
import {
  CourseRate,
  PublicHoliday,
  Days,
  getActiveClosures,
  BREAKPOINTS,
  GolfContractProduct,
  GolfContract,
  getCoursePricing,
} from "@kalabo/golfsavers-common";
import { useApp } from "@store/app";
import { useCheckout } from "@store/checkout";
import { useDocument } from "@hooks/useDocument";
import { Button, Text } from "@uikit";
import XE from "@components/common/xe";
import { IoCloseOutline } from "@react-icons/all-files/io5/IoCloseOutline";
import supabase from "@clients/supabase";
import dayjs from "dayjs";
import { RateWarning } from "./rateWarning";
import { RateBlock } from "./rateBlock";
import DateField from "@uikit/datefield";
import { Select } from "antd";
import useBreakpoint from "use-breakpoint";

interface RateSearchProps {
  agent: any;
  onSelected?: (rate: CourseRate) => void;
  contracts: any;
  private_club: boolean;
  course_id: string;
  public_holidays?: PublicHoliday[];
  weekend_days?: Days;
}

const RateSearch = ({
  agent,
  onSelected,
  contracts,
  private_club,
  public_holidays,
  course_id,
  weekend_days,
}: RateSearchProps) => {
  const date = useApp(($) => $.date);
  const setDate = useApp(($) => $.setDate);
  const setShowPeoplePicker = useApp(($) => $.setShowPeoplePicker);
  const setShowDatePicker = useApp(($) => $.setShowDatePicker);
  const golfers = useApp(($) => $.golfers);
  const setGolfers = useApp(($) => $.setGolfers);
  const display_currency = useApp(($) => $.display_currency);

  const [loading, setLoading] = React.useState(true);
  const rateRef = React.useRef(null);

  const [mode, setMode] = React.useState<any>(null);

  const doc = useDocument();
  const [isSticky, setSticky] = React.useState(true);
  const [expanded, isExpanded] = React.useState<boolean>(false);
  const [course_warnings, setCourseWarnings] = React.useState<any | null>(null);
  const [course_closures, setCourseClosures] = React.useState<any | null>(null);

  const { breakpoint } = useBreakpoint(BREAKPOINTS, "mobile");
  // use effect on expanded to add no scroll to body

  React.useEffect(() => {
    if (expanded) {
      doc && doc.body.classList.add("overflow-hidden");
    } else {
      doc && doc.body.classList.remove("overflow-hidden");
    }
  }, [expanded, doc]);

  React.useEffect(() => {
    // declare the data fetching function
    const fetchData = async () => {
      await loadCourseData();
    };

    fetchData();
  }, [course_id]);

  React.useEffect(() => {
    switch (mode?.message) {
      case "NO_RATES_EVER":
      case "NO_CURRENT_RATES":
      case "CLOSED_FURTHER_NOTICE_NO_END_DATE":
      case "CLOSED_FURTHER_NOTICE_WITH_END_DATE_WITHOUT_RATES":
        setSticky(false);
        break;
      default:
        setSticky(true);
    }
  }, [mode]);

  const cheapest_package = React.useMemo(() => {
    return getCoursePricing(null, contracts) || 0;
  }, [contracts, date]);

  const active_closure = React.useMemo(() => {
    let closure = getActiveClosures(date, course_closures);
    return closure.length > 0 ? closure[0] : null;
  }, [date, course_closures]);

  const isEndOfDayClosure = React.useMemo(() => {
    // if the closure end date is set and the end_time is EQUAL or AFTER 23:55 then it is an end of day closure
    return (
      active_closure?.end_date &&
      active_closure?.end_time &&
      dayjs(active_closure?.end_time, "HH:mm").isSameOrAfter(
        dayjs("23:55", "HH:mm")
      )
    );
  }, [active_closure]);

  const loadCourseData = React.useCallback(async () => {
    setLoading(true);

    let promises: any[] = [];

    promises.push(
      new Promise(async (resolve, reject) => {
        resolve(
          supabase
            .from("course_closures")
            .select("*")
            .eq("course_id", course_id)
        );
      })
    );
    promises.push(
      new Promise(async (resolve, reject) => {
        resolve(
          supabase
            .from("course_warnings")
            .select("*")
            .eq("course_id", course_id)
        );
      })
    );

    Promise.all(promises)
      .then((a: any) => {
        setCourseClosures(a[0].data);
        setCourseWarnings(a[1].data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [course_id]);

  const renderMobileFooter = React.useMemo(() => {
    if (!mode || mode.message === "LOADING")
      return (
        <div className="bg-white flex flex-1 p-2 justify-center">
          {" "}
          <svg
            aria-hidden="true"
            className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="#ddd"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        </div>
      );

    switch (mode?.message) {
      case "CLOSED_PERMANENTLY":
        return (
          <div className="bg-red flex flex-1 p-2 justify-center">
            <Text size={19} weight="semibold" color="white" leading={1.3}>
              Permanently Closed
            </Text>
          </div>
        );
      case "CLOSED_FURTHER_NOTICE_NO_END_DATE":
        return (
          <div className="flex flex-col gap-3 flex-1">
            <Text
              size={19}
              weight="semibold"
              color="black"
              leading={1.3}
              className="text-center"
            >
              **CLOSED UNTIL FURTHER NOTICE**
            </Text>
            <Button
              onClick={() => {
                isExpanded(true);
              }}
              size="large"
              color="green"
              width="full"
              className={`!mb-0`}
              text="Notify me when course reopens"
            />
          </div>
        );
      case "CLOSED_FURTHER_NOTICE_WITH_END_DATE_WITHOUT_RATES": {
        let end_date = dayjs(active_closure?.end_date)
          .add(isEndOfDayClosure ? 1 : 0, "day")
          .format("DD MMM YYYY");
        return (
          <div className="flex flex-col gap-3 flex-1">
            <Text
              size={19}
              weight="semibold"
              color="black"
              leading={1.3}
              className="text-center uppercase"
            >
              **RE-OPENING ON {end_date}**
            </Text>
            <Button
              onClick={() => {
                isExpanded(true);
              }}
              size="large"
              color="green"
              width="full"
              className={`!mb-0`}
              text={`Notify me when tee times ${
                breakpoint !== "mobile" ? "are" : ""
              } available`}
            />
          </div>
        );
      }
      case "CLOSED_FURTHER_NOTICE_WITH_END_DATE_WITH_RATES": {
        let end_date = dayjs(active_closure?.end_date)
          .add(isEndOfDayClosure ? 1 : 0, "day")
          .format("DD MMM YYYY");
        return (
          <div className="flex flex-col gap-3 flex-1">
            <Text
              size={19}
              weight="semibold"
              color="black"
              leading={1.3}
              className="text-center uppercase"
            >
              **RE-OPENING ON {end_date}**
            </Text>
            <Button
              onClick={() => {
                isExpanded(true);
              }}
              size="large"
              color="green"
              width="full"
              className={`!mb-0`}
              text="View first available tee times"
            />
          </div>
        );
      }
      case "NO_CURRENT_RATES":
      case "NO_RATES_EVER": {
        return (
          <div className="flex flex-col gap-3 flex-1">
            <Button
              onClick={() => {
                isExpanded(true);
              }}
              size="large"
              color="green"
              width="full"
              className={`!mb-0`}
              text={`Notify me when tee times ${
                breakpoint !== "mobile" ? "are" : ""
              } available`}
            />
          </div>
        );
      }
      default:
        return (
          <>
            <div className="flex flex-col gap-1 flex-1">
              <Text size={11} italic color="black60" leading={1.3}>
                from
              </Text>
              <div className="flex items-end gap-2">
                <Text size={19} weight="bold" color="black" leading={1}>
                  <XE
                    base_currency={cheapest_package.currency}
                    currency={display_currency.code}
                    value={cheapest_package.price}
                  />
                </Text>
                <Text size={13} color="black60" leading={1.3}>
                  / 18 Holes
                </Text>
              </div>
            </div>
            <div>
              <Button
                onClick={() => {
                  isExpanded(true);
                }}
                size="large"
                color="green"
                width="full"
                className={`!mb-0`}
                text="Book Now"
              />
            </div>
          </>
        );
    }
  }, [
    cheapest_package,
    contracts,
    display_currency,
    isEndOfDayClosure,
    active_closure,
    mode,
    breakpoint,
  ]);

  const header = () => {
    return (
      <div className="flex gap-2 py-4 px-5 sm:px-10 lg:px-5  h-[94px] ">
        <div className="flex flex-1 flex-col border rounded-md p-2 items-center border-grey2 shadow-md cursor-pointer">
          <Text size={13} leading={1.3} weight="semibold" className="mb-0">
            Date of play
          </Text>
          <div
            onClick={() => {
              if (breakpoint === "mobile") {
                setShowDatePicker();
              }
            }}
          >
            {breakpoint === "mobile" ? (
              <>
                <Text
                  size={16}
                  leading={1.3}
                  weight="semibold"
                  color="blue"
                  className="mb-0 mt-[2px]"
                >
                  {!date
                    ? "Select date"
                    : dayjs(date).format("ddd, DD MMM YYYY")}
                </Text>
              </>
            ) : (
              <>
                <DateField
                  size={"large"}
                  id="arrival_date"
                  allowClear={false}
                  simpleMode
                  format="ddd, DD MMM YYYY"
                  className="p-0 m-0 [&.ant-picker_.ant-picker-input_>_input]:text-16 font-semibold text-center text-blue leading-[150%] caret-transparent pt-0.5"
                  onChange={(e) => {
                    setDate(e);
                  }}
                  value={date ? dayjs(date).toDate() : dayjs().toDate()}
                />
              </>
            )}
          </div>
        </div>
        <div className="flex flex-1 flex-col border rounded-md  p-2 items-center border-grey2 shadow-md cursor-pointer">
          <Text size={13} leading={1.3} weight="semibold" className="mb-0">
            No. of players
          </Text>
          <div
            onClick={() => {
              if (breakpoint === "mobile") {
                setShowPeoplePicker();
              }
            }}
          >
            {breakpoint === "mobile" ? (
              <>
                <Text
                  size={16}
                  leading={1.3}
                  weight="semibold"
                  color="blue"
                  className="mb-0 mt-[2px]"
                >
                  {!golfers
                    ? "Select players"
                    : `${golfers || 1} player${golfers > 1 ? "s" : ""}`}
                </Text>
              </>
            ) : (
              <>
                <Select
                  bordered={false}
                  style={{ width: "100%", textAlign: "center", height: "27px" }}
                  className="!p-0 !m-0 [&_.ant-select-selection-item]:text-16 [&_.ant-select-arrow]:hidden [&_.ant-select-selection-item]:!pe-0 [&_.ant-select-selection-item]:!font-semibold [&_.ant-select-selection-item]:text-center [&_.ant-select-selection-item]:text-blue"
                  onChange={(e) => {
                    setGolfers(Number(e));
                  }}
                  value={golfers}
                >
                  {[...Array(16)].map((_, i) => (
                    <Select.Option key={i} value={i + 1}>
                      {i + 1} player{i + 1 > 1 && "s"}
                    </Select.Option>
                  ))}
                </Select>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const stickyClass = React.useCallback(() => {
    if (isSticky) {
      return "fixed lg:sticky  lg:top-[90px] bottom-0 left-0 right-0 lg:bottom-auto z-[999] lg:z-[800] lg:mb-11";
    } else {
      return "fixed lg:relative bottom-0 left-0 right-0 z-[999] lg:z-[800] lg:mb-11";
    }
  }, [isSticky]);

  return (
    <>
      <div className={`${stickyClass()}   `}>
        <div
          className={` bg-white lg:rounded-lg overflow-hidden shadow-standard transition-[height] ${
            expanded ? "h-[100dvh]" : "h-0 lg:h-auto"
          }`}
        >
          <div className="bg-blue text-center px-4 py-4 pb-4 lg:pt-5 relative">
            <a id="book" />
            <Text
              size={19}
              sizeTablet={21}
              sizeDesktop={21}
              align="center"
              color="white"
              leading={1.3}
              element="h2"
              weight="semibold"
            >
              Book a Tee Time
            </Text>
            <div className="lg:hidden absolute right-5 sm:right-10 top-3">
              <button
                type="button"
                className="-m-2 p-2 rounded-md inline-flex items-center justify-center text-gray-400"
                onClick={() => isExpanded(false)}
              >
                <span className="sr-only">Close menu</span>
                <IoCloseOutline
                  className="h-8 w-8 text-white"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
          <div className="relative bg-white">
            {header()}

            {loading ? (
              <>
                {" "}
                <div className="min-h-[200px] inset-0  opacity-80 z-10 flex justify-center items-center">
                  <svg
                    aria-hidden="true"
                    className="w-12 h-12 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-green"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="#ddd"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                </div>
              </>
            ) : (
              <div
                className={`px-5 sm:px-10 lg:px-5 pb-4 overflow-auto ${
                  isSticky
                    ? `max-h-[calc(100dvh-151px)] md:max-h-[calc(100dvh-260px)]`
                    : `max-h-[calc(100dvh-151px)] md:max-h-none`
                } `}
              >
                {!date || !golfers ? (
                  <RateWarning>
                    <Text
                      size={17}
                      leading={1.3}
                      weight="semibold"
                      className="text-center"
                    >
                      To display our latest rates, please select a date of play
                      and the number of players
                    </Text>
                  </RateWarning>
                ) : (
                  <>
                    <RateBlock
                      agent={agent}
                      onModeChange={(mode) => {
                        setMode(mode);
                      }}
                      private_club={private_club}
                      onSelected={onSelected}
                      contracts={contracts}
                      display_currency={display_currency}
                      public_holidays={public_holidays}
                      closures={course_closures}
                      warnings={course_warnings}
                      course_id={course_id}
                      number_of_golfers={golfers}
                      date={date}
                      weekend_days={weekend_days}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div
          className={`${
            expanded && "hidden"
          } flex gap-1 p-5 md:px-10 bg-white border-t border-grey3 lg:hidden`}
        >
          {renderMobileFooter}
        </div>
      </div>
    </>
  );
};

export default RateSearch;
